import {
	Icon,
	IconButton,
	IconColor,
	Select,
	SelectItem,
	SelectReadOnly,
	ValidationError
} from "client/Components";
import { ResultMapperColor } from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { IObjectInputAttachable, IValueInputAttachable } from "client/Forms/attachable";
import {
	ReactElement,
	useCallback
} from "react";
import { ExpressionTextInput } from "./ExpressionTextInput";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./ColorSelector.module.less";
import { setTrackingId } from "client/Misc/utils";
import {
	ResultMapperColorConditionModelFormData,
	ResultMapperStyledFieldFormData
} from "./common";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { getClientId, setClientId } from "client/Misc/clientId";

export type ColorSelectorProps = {
	attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>;
	isReadOnly: boolean;
	activeConditionIndex?: number | null;
};

export const ColorSelector = (props: ColorSelectorProps): ReactElement => {
	const {
		attachable,
		isReadOnly,
		activeConditionIndex
	} = props;

	const colorsAttachable: IValueInputAttachable<readonly ResultMapperColorConditionModelFormData[]> = attachable.for("colors");

	const addRowHandler = useCallback(
		(): void => {
			colorsAttachable.reducer
				.push(createDefaultColorCondition());

			colorsAttachable.validation
				.triggerInputFinished();
		},
		[
			colorsAttachable.reducer,
			colorsAttachable.validation
		]
	);

	const removeRowHandler = useCallback(
		(index: number): void => {
			const isRemovingLastColor: boolean = colorsAttachable.value
				.length === 1;

			colorsAttachable.reducer
				.removeAt(index);

			colorsAttachable.validation
				.triggerInputFinished();

			if (isRemovingLastColor) {
				attachable.for("shouldIncludeColors")
					.reducer
					.setValue(false);
			}
		},
		[
			attachable,
			colorsAttachable
		]
	);

	const trackingId: string = colorsAttachable.attachToSelect()
		.trackingId;

	return (
		<div
			{...setTrackingId(trackingId)}
		>
			<h4 className={styles.colorsTitle}>
				{localize("_Colors")}
			</h4>

			<ul className={styles.colorsList}>
				{colorsAttachable.map(
					(rowAttachable, index): ReactElement => (
						<ColorRow
							key={getClientId(rowAttachable.value)}
							rowAttachable={rowAttachable}
							index={index}
							activeConditionIndex={activeConditionIndex}
							isReadOnly={isReadOnly}
							onRemove={removeRowHandler}
						/>
					)
				)}
			</ul>
			{!isReadOnly && (
				<div className={styles.addColorRowButtonContainer}>
					<IconButton
						kind="dashed"
						icon="plus"
						width="fill"
						trackingId="add-color-row"
						onClick={addRowHandler}
					/>
				</div>
			)}
		</div>
	);
};
ColorSelector.displayName = "ColorSelector";

type ColorRowProps = {
	rowAttachable: IValueInputAttachable<ResultMapperColorConditionModelFormData>;
	index: number;
	activeConditionIndex?: number | null;
	isReadOnly: boolean;
	onRemove: (index: number) => void;
};

const ColorRow = (props: ColorRowProps): ReactElement => {
	const {
		rowAttachable,
		index,
		activeConditionIndex,
		isReadOnly,
		onRemove
	} = props;

	return (
		<li className={styles.colorRow}>
			<div className={styles.activeConditionIndicator}>
				{activeConditionIndex === index && (
					<Icon
						value={{
							kind: "font",
							name: "check",
							color: "success"
						}}
						size="medium"
					/>
				)}
			</div>
			<div className={styles.colorRowContent}>
				<div className={styles.selectContainer}>
					{isReadOnly ? (
						<SelectReadOnly
							{...rowAttachable.for("color").attachToSelect()}
							options={colorOptions}
						/>
					) : (
						<Select
							{...rowAttachable.for("color").attachToSelect()}
							options={colorOptions}
							placeholder={localize("_SelectColor")}
							menuTitle={localize("_SelectColor")}
						/>
					)}
					<ValidationError {...rowAttachable.for("color").attachToValidationError()} />
				</div>
				<div className={styles.expressionInputContainer}>
					<ExpressionTextInput
						attachable={rowAttachable.for("conditionExpression")}
						placeholder={localize("_EnterCondition")}
						isReadOnly={isReadOnly}
					/>
					<ValidationError {...rowAttachable.for("conditionExpression").attachToValidationError()} />
				</div>
				{!isReadOnly && (
					<IconButton
						kind="secondary"
						icon="minus"
						trackingId="remove-color-row"
						onClick={() => onRemove(index)}
					/>
				)}
			</div>
		</li>
	);
};
ColorRow.displayName = "ColorRow";

export function createDefaultColorCondition(): ResultMapperColorConditionModelFormData {
	return setClientId({
		color: null,
		conditionExpression: ""
	});
}

export const appResultMapperColorToClassNameMapping: Record<ResultMapperColor, IconColor> = {
	[ResultMapperColor.Green]: "success",
	[ResultMapperColor.Blue]: "info",
	[ResultMapperColor.Orange]: "warning",
	[ResultMapperColor.Red]: "danger",
	[ResultMapperColor.Grey]: "secondary",
	[ResultMapperColor.Default]: "primary-text",
	[ResultMapperColor.Purple]: "purple",
	[ResultMapperColor.Cyan]: "cyan"
};

const unsortedColorOptions: readonly SelectItem<ResultMapperColor>[] = [
	{
		text: localize("_Green"),
		value: ResultMapperColor.Green,
		icon: {
			kind: "font",
			name: "square",
			color: appResultMapperColorToClassNameMapping[ResultMapperColor.Green]
		}

	},
	{
		text: localize("_Blue"),
		value: ResultMapperColor.Blue,
		icon: {
			kind: "font",
			name: "square",
			color: appResultMapperColorToClassNameMapping[ResultMapperColor.Blue]
		}
	},
	{
		text: localize("_Orange"),
		value: ResultMapperColor.Orange,
		icon: {
			kind: "font",
			name: "square",
			color: appResultMapperColorToClassNameMapping[ResultMapperColor.Orange]
		}
	},
	{
		text: localize("_Red"),
		value: ResultMapperColor.Red,
		icon: {
			kind: "font",
			name: "square",
			color: appResultMapperColorToClassNameMapping[ResultMapperColor.Red]
		}
	},
	{
		text: localize("_Grey"),
		value: ResultMapperColor.Grey,
		icon: {
			kind: "font",
			name: "square",
			color: appResultMapperColorToClassNameMapping[ResultMapperColor.Grey]
		}
	},
	{
		text: localize("_Default"),
		value: ResultMapperColor.Default,
		icon: {
			kind: "font",
			name: "square",
			color: appResultMapperColorToClassNameMapping[ResultMapperColor.Default]
		}
	},
	{
		text: localize("_Purple"),
		value: ResultMapperColor.Purple,
		icon: {
			kind: "font",
			name: "square",
			color: appResultMapperColorToClassNameMapping[ResultMapperColor.Purple]
		}
	},
	{
		text: localize("_Cyan"),
		value: ResultMapperColor.Cyan,
		icon: {
			kind: "font",
			name: "square",
			color: appResultMapperColorToClassNameMapping[ResultMapperColor.Cyan]
		}
	}
];

const colorOptions: readonly SelectItem<ResultMapperColor>[] = sortBy(
	unsortedColorOptions,
	x => x.text
);
